import { useMemo, useState } from 'react';
import { displayBonusLabel, type BonusType, type BonusZone, type TodayBonusRow } from '../../api/bonuses';
import { findExistingZoneAssignment, isUnconfirmedAssignment } from './resourceHunterUtils';
import { ReplacementConfirmPopover } from './ReplacementConfirmPopover';

type ResourceBonusAddPanelProps = {
    zones: BonusZone[];
    bonusTypes: BonusType[];
    currentRows: TodayBonusRow[];
    canEdit: boolean;
    isSubmitting: boolean;
    errorMessage?: string | null;
    onSubmit: (zoneId: string, bonusTypeId: string) => void;
};

export function ResourceBonusAddPanel({
    zones,
    bonusTypes,
    currentRows,
    canEdit,
    isSubmitting,
    errorMessage,
    onSubmit
}: ResourceBonusAddPanelProps) {
    const [zoneSearch, setZoneSearch] = useState('');
    const [selectedBonusTypeId, setSelectedBonusTypeId] = useState('');
    const [pendingReplacement, setPendingReplacement] = useState<null | {
        zoneId: string;
        zoneName: string;
        fromBonusLabel: string;
        toBonusTypeId: string;
        toBonusLabel: string;
    }>(null);

    const selectedZone = useMemo(() => {
        const normalized = zoneSearch.trim().toLowerCase();
        return zones.find((zone) => zone.name.toLowerCase() === normalized || zone.slug.toLowerCase() === normalized) ?? null;
    }, [zoneSearch, zones]);

    const selectedBonusType = bonusTypes.find((type) => type.id === selectedBonusTypeId) ?? null;

    function handleSubmit() {
        if (!selectedZone || !selectedBonusType) {
            return;
        }

        const existing = findExistingZoneAssignment(currentRows, selectedZone.id);
        if (existing && !isUnconfirmedAssignment(existing) && existing.bonusTypeId !== selectedBonusType.id) {
            setPendingReplacement({
                zoneId: selectedZone.id,
                zoneName: selectedZone.name,
                fromBonusLabel: existing.bonusLabel,
                toBonusTypeId: selectedBonusType.id,
                toBonusLabel: displayBonusLabel(selectedBonusType)
            });
            return;
        }

        setPendingReplacement(null);
        onSubmit(selectedZone.id, selectedBonusType.id);
    }

    function handleConfirmReplacement() {
        if (!pendingReplacement) {
            return;
        }

        onSubmit(pendingReplacement.zoneId, pendingReplacement.toBonusTypeId);
        setPendingReplacement(null);
    }

    if (!canEdit) {
        return null;
    }

    return (
        <section className="report-panel resource-add-panel mb-4">

            <div className="row g-3 align-items-end">
                <div className="col-lg-4">
                    <label className="form-label" htmlFor="bonus-zone-search">
                        Zone
                    </label>
                    <input
                        id="bonus-zone-search"
                        className="form-control"
                        list="bonus-zone-options"
                        placeholder="Enter Zone name..."
                        value={zoneSearch}
                        onChange={(event) => {
                            setZoneSearch(event.target.value);
                            setPendingReplacement(null);
                        }}
                    />
                    <datalist id="bonus-zone-options">
                        {zones.map((zone) => {
                            const metadata = [
                                zone.expansion,
                                zone.minLevel != null || zone.maxLevel != null ? `Lv ${zone.minLevel ?? '?'}-${zone.maxLevel ?? '?'}` : null,
                                zone.zoneType
                            ]
                                .filter(Boolean)
                                .join(' · ');

                            return <option key={zone.id} value={zone.name} label={zone.name} />;
                        })}
                    </datalist>
                </div>

                <div className="col-lg-6">
                    <div className="form-label">Bonus type</div>
                    <div className="resource-bonus-button-row" role="group" aria-label="Bonus type">
                        {bonusTypes.map((type) => (
                            <button
                                key={type.id}
                                className={`btn btn-sm ${selectedBonusTypeId === type.id ? 'btn-light' : 'btn-outline-light'}`}
                                type="button"
                                onClick={() => {
                                    setSelectedBonusTypeId(type.id);
                                    setPendingReplacement(null);
                                }}
                            >
                                {displayBonusLabel(type)}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="col-lg-2 d-flex flex-column align-self-stretch">
                    
                        <button className="btn btn-success mt-auto add-zone-btn" type="button" disabled={!selectedZone || !selectedBonusType || isSubmitting} onClick={handleSubmit}>
                            Add
                        </button>
                    
                </div>
            </div>


            {pendingReplacement ? (
                <ReplacementConfirmPopover
                    zoneName={pendingReplacement.zoneName}
                    fromBonusLabel={pendingReplacement.fromBonusLabel}
                    toBonusLabel={pendingReplacement.toBonusLabel}
                    isSubmitting={isSubmitting}
                    onCancel={() => setPendingReplacement(null)}
                    onConfirm={handleConfirmReplacement}
                />
            ) : null}

            {zoneSearch && !selectedZone ? <p className="small text-warning mt-3 mb-0">Select an exact zone from the autocomplete list.</p> : null}
            {errorMessage ? <p className="small text-danger mt-3 mb-0">{errorMessage}</p> : null}
        </section>
    );
}
